import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../api/rpc'
import { Button } from '../components/ui/Button'
import { Badge, EmptyState } from '../components/ui/Card'
import { Field, Input, Textarea } from '../components/ui/Field'
import { Select } from '../components/ui/Select'
import { Modal } from '../components/ui/Modal'
import { useToast } from '../components/ui/Toast'
import { useWorkspaces, WorkspacePicker } from '../components/useWorkspaces'
import { cn } from '../lib/cn'
import { confirmDialog } from '../components/ui/Confirm'
import { Count, FilterChips, ModalFooter, Pager, Row, RowMenu, SearchBox, Table, Td, Th, Toolbar, useTable } from '../components/ui/DataTable'
import type { MenuItem } from '../components/ui/Popover'
import { formatDateTime } from '../lib/format'

type MemType = 'user' | 'feedback' | 'project' | 'reference'
const MEM_TYPES: MemType[] = ['user', 'feedback', 'project', 'reference']
interface Fact { file: string; name: string; description: string; type: string | null; updatedAt: number; valid: boolean }
interface SkillInfo { name: string; description: string; source: 'builtin' | 'user' | 'workspace'; editable: boolean }
interface FactDraft { file?: string; name: string; description: string; type: MemType; body: string }
interface SkillDraft { name: string; description: string; content: string; existing: boolean; readOnly: boolean }

const bodyOf = (content: string) => content.replace(/^---\n[\s\S]*?\n---\n*/, '')
const factSearch = (f: Fact): string => `${f.name} ${f.description}`
const skillSearch = (s: SkillInfo): string => `${s.name} ${s.description}`

/** Panel header: eyebrow, title, one-line lede, status line and the primary action (composed locally). */
export function PanelHead({ eyebrow, title, lede, status, action }: {
  eyebrow: string; title: string; lede: string; status: ReactNode; action?: ReactNode
}) {
  return (
    <header className="mb-5 flex flex-wrap items-end justify-between gap-6 border-b border-line pb-5">
      <div className="min-w-0">
        <div className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-500">{eyebrow}</div>
        <h1 className="mt-2 font-display text-[30px] font-bold leading-tight tracking-tight">{title}</h1>
        <p className="mt-2 max-w-[70ch] text-sm text-ink-400">{lede}</p>
        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13.5px] text-ink-400">{status}</p>
      </div>
      {action && <div className="flex items-center gap-2">{action}</div>}
    </header>
  )
}

export function Memory() {
  const { t, i18n } = useTranslation()
  const toast = useToast()
  const { items: workspaces } = useWorkspaces()
  const [ws, setWs] = useState('')
  const [facts, setFacts] = useState<Fact[] | null>(null)
  const [draft, setDraft] = useState<FactDraft | null>(null)
  const readOnly = workspaces.find(w => w.id === ws)?.role === 'viewer'
  const params = ws ? { workspaceId: ws } : {}
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])
  const load = useCallback(
    () => rpc.call<{ items: Fact[] }>('memory.list', ws ? { workspaceId: ws } : {}).then(r => setFacts(r.items)).catch(fail),
    [ws, fail]
  )
  useEffect(() => { void load() }, [load])

  const table = useTable(facts, { search: factSearch, filter: (f, v) => f.type === v })
  const newest = facts && facts.length ? facts.reduce((m, f) => Math.max(m, f.updatedAt), 0) : null

  const open = async (f: Fact) => {
    try {
      const r = await rpc.call<{ content: string }>('memory.get', { ...params, file: f.file })
      setDraft({ file: f.file, name: f.name, description: f.description, type: (f.type as MemType) ?? 'user', body: bodyOf(r.content) })
    } catch (err) { fail(err) }
  }
  const remove = async (f: Fact) => {
    if (!await confirmDialog(t('knowledge.confirmDeleteMemory', { name: f.name }))) return
    try { await rpc.call('memory.delete', { ...params, file: f.file }); void load() } catch (err) { fail(err) }
  }
  const save = async () => {
    if (!draft) return
    try { await rpc.call('memory.save', { ...params, ...draft }); setDraft(null); void load() } catch (err) { fail(err) }
  }
  const menu = (f: Fact): MenuItem[] => [
    { label: t('common.edit'), onSelect: () => void open(f) },
    ...(!readOnly ? [{ label: t('common.delete'), danger: true, divider: true, onSelect: () => void remove(f) }] : [])
  ]
  const filters: { value: string; label: string }[] = [
    { value: 'all', label: t('common.all') },
    ...MEM_TYPES.map(x => ({ value: x, label: t(`knowledge.type_${x}`) }))
  ]

  return (
    <div className="space-y-5">
      <PanelHead
        eyebrow={`${t('settings.group.workspace')} / ${t('settings.nav.memory')}`}
        title={t('knowledge.memory')}
        lede={t('knowledge.memoryHint')}
        status={<>
          <b>{t('knowledge.count', { count: facts?.length ?? 0 })}</b>
          {newest !== null && <>
            <span className="text-ink-500">·</span>
            <span>{t('knowledge.colUpdated')} <b>{formatDateTime(newest, i18n.language)}</b></span>
          </>}
        </>}
        action={!readOnly ? <Button onClick={() => setDraft({ name: '', description: '', type: 'user', body: '' })}>{t('knowledge.addMemory')}</Button> : undefined}
      />
      <Toolbar>
        <WorkspacePicker items={workspaces} value={ws} onChange={setWs} />
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('knowledge.searchMemory')} />
        <FilterChips value={table.filter} onChange={table.setFilter} label={t('knowledge.type')} options={filters} />
        <Count>{t('knowledge.count', { count: table.total })}</Count>
      </Toolbar>
      {facts?.length === 0 ? (
        <EmptyState title={t('knowledge.noMemory')} body={t('knowledge.noMemoryBody')} />
      ) : (
        <Table footer={<span className="ml-auto"><Pager page={table.page} pages={table.pages} onChange={table.setPage} /></span>}>
          <thead>
            <tr>
              <Th rail />
              <Th>{t('knowledge.name')}</Th>
              <Th>{t('knowledge.type')}</Th>
              <Th>{t('knowledge.colSummary')}</Th>
              <Th>{t('knowledge.colUpdated')}</Th>
              <Th><span className="sr-only">{t('common.actions')}</span></Th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(f => (
              <Row key={f.file} rail={f.valid ? undefined : 'attention'} onClick={() => void open(f)}>
                <Td><span className="font-mono text-[12.5px] font-semibold">{f.name}</span></Td>
                <Td>{f.type ? <Badge>{t(`knowledge.type_${f.type}`, { defaultValue: f.type })}</Badge> : <span className="text-ink-500">—</span>}</Td>
                <Td className="text-ink-500">{f.description}</Td>
                <Td className="text-ink-500">{formatDateTime(f.updatedAt, i18n.language)}</Td>
                <Td><RowMenu items={menu(f)} /></Td>
              </Row>
            ))}
          </tbody>
        </Table>
      )}
      <Modal open={draft !== null} title={draft?.file ? draft.name : t('knowledge.addMemoryTitle')}
        description={draft?.file ? undefined : t('knowledge.factHint')} onClose={() => setDraft(null)}
        footer={!readOnly ? <ModalFooter onCancel={() => setDraft(null)} onSubmit={() => void save()} disabled={!draft?.name.trim()} /> : undefined}>
        {draft && <div className="space-y-3">
          <Field label={t('knowledge.name')}>
            {id => <Input id={id} value={draft.name} disabled={Boolean(draft.file) || readOnly} onChange={e => setDraft({ ...draft, name: e.target.value })} />}
          </Field>
          <Field label={t('knowledge.description')}>
            {id => <Input id={id} value={draft.description} disabled={readOnly} onChange={e => setDraft({ ...draft, description: e.target.value })} />}
          </Field>
          <Field label={t('knowledge.type')}>
            {id => (
              <Select
                id={id} value={draft.type} disabled={readOnly}
                onChange={v => setDraft({ ...draft, type: v })}
                options={MEM_TYPES.map(x => ({ value: x, label: t(`knowledge.type_${x}`) }))}
              />
            )}
          </Field>
          <Field label={t('knowledge.content')}>
            {id => <Textarea id={id} rows={8} value={draft.body} disabled={readOnly} onChange={e => setDraft({ ...draft, body: e.target.value })} />}
          </Field>
        </div>}
      </Modal>
    </div>
  )
}

export function Skills() {
  const { t } = useTranslation()
  const toast = useToast()
  const [items, setItems] = useState<SkillInfo[] | null>(null)
  const [edit, setEdit] = useState<SkillDraft | null>(null)
  const fail = useCallback((err: unknown) => toast((err as Error).message, 'error'), [toast])
  const load = useCallback(() => rpc.call<{ items: SkillInfo[] }>('skills.list').then(r => setItems(r.items)).catch(fail), [fail])
  useEffect(() => { void load() }, [load])

  const table = useTable(items, { search: skillSearch, filter: (s, v) => s.source === v })
  const counts = useMemo(() => {
    const c = { user: 0, builtin: 0, workspace: 0 }
    for (const s of items ?? []) c[s.source] += 1
    return c
  }, [items])

  const open = async (s: SkillInfo) => {
    try {
      const r = await rpc.call<{ description: string; content: string; source: string }>('skills.get', { name: s.name })
      setEdit({ name: s.name, description: r.description, content: r.content, existing: true, readOnly: r.source !== 'user' })
    } catch (err) { fail(err) }
  }
  const remove = async (s: SkillInfo) => {
    if (!await confirmDialog(t('knowledge.confirmDeleteSkill', { name: s.name }))) return
    try { await rpc.call('skills.delete', { name: s.name }); void load() } catch (err) { fail(err) }
  }
  const save = async () => {
    if (!edit) return
    try { await rpc.call('skills.save', { name: edit.name, description: edit.description, content: edit.content }); setEdit(null); void load() } catch (err) { fail(err) }
  }
  const menu = (s: SkillInfo): MenuItem[] => [
    { label: t('common.edit'), onSelect: () => void open(s) },
    ...(s.editable ? [{ label: t('common.delete'), danger: true, divider: true, onSelect: () => void remove(s) }] : [])
  ]
  const filters: { value: string; label: string }[] = [
    { value: 'all', label: t('common.all') },
    { value: 'user', label: t('knowledge.source_user') },
    { value: 'builtin', label: t('knowledge.source_builtin') },
    { value: 'workspace', label: t('knowledge.source_workspace') }
  ]

  return (
    <div className="space-y-5">
      <PanelHead
        eyebrow={`${t('settings.group.workspace')} / ${t('settings.nav.skills')}`}
        title={t('knowledge.skills')}
        lede={t('knowledge.skillsHint')}
        status={<>
          <b>{t('knowledge.skillCount', { count: items?.length ?? 0 })}</b>
          <span className="text-ink-500">·</span>
          <b>{counts.user}</b> {t('knowledge.source_user')}
          <span className="text-ink-500">·</span>
          <b>{counts.builtin}</b> {t('knowledge.source_builtin')}
          <span className="text-ink-500">·</span>
          <b>{counts.workspace}</b> {t('knowledge.source_workspace')}
        </>}
        action={<Button onClick={() => setEdit({ name: '', description: '', content: '', existing: false, readOnly: false })}>{t('knowledge.addSkill')}</Button>}
      />
      <Toolbar>
        <SearchBox value={table.query} onChange={table.setQuery} placeholder={t('knowledge.searchSkills')} />
        <FilterChips value={table.filter} onChange={table.setFilter} label={t('knowledge.source')} options={filters} />
        <Count>{t('knowledge.skillCount', { count: table.total })}</Count>
      </Toolbar>
      <Table footer={<span className="ml-auto"><Pager page={table.page} pages={table.pages} onChange={table.setPage} /></span>}>
        <thead>
          <tr>
            <Th rail />
            <Th>{t('knowledge.name')}</Th>
            <Th>{t('knowledge.source')}</Th>
            <Th>{t('knowledge.colWhenToUse')}</Th>
            <Th><span className="sr-only">{t('common.actions')}</span></Th>
          </tr>
        </thead>
        <tbody>
          {table.rows.map(s => (
            <Row key={`${s.source}:${s.name}`} rail={s.editable ? 'ok' : 'idle'} onClick={() => void open(s)}>
              <Td><span className="font-mono text-[12.5px] font-semibold">{s.name}</span></Td>
              <Td><Badge tone={s.source === 'user' ? 'accent' : 'neutral'}>{t(`knowledge.source_${s.source}`)}</Badge></Td>
              <Td className="text-ink-500">{s.description}</Td>
              <Td><RowMenu items={menu(s)} /></Td>
            </Row>
          ))}
        </tbody>
      </Table>
      <Modal open={edit !== null} title={edit?.existing ? edit.name : t('knowledge.addSkillTitle')}
        description={t('knowledge.skillHint')} onClose={() => setEdit(null)}
        footer={<ModalFooter onCancel={() => setEdit(null)} onSubmit={() => void save()}
          submitLabel={edit?.readOnly ? t('knowledge.customize') : t('common.save')}
          disabled={!edit || !edit.name.trim() || !edit.description.trim() || !edit.content.trim()} />}>
        {edit && <div className="space-y-3">
          {edit.readOnly && <p className="rounded-lg bg-cream-100 px-3 py-2 text-xs text-ink-400">{t('knowledge.builtinHint')}</p>}
          <Field label={t('knowledge.name')}>
            {id => <Input id={id} value={edit.name} disabled={edit.existing} onChange={e => setEdit({ ...edit, name: e.target.value })} />}
          </Field>
          <Field label={t('knowledge.whenToUse')}>
            {id => <Input id={id} value={edit.description} onChange={e => setEdit({ ...edit, description: e.target.value })} />}
          </Field>
          <Field label={t('knowledge.instructions')}>
            {id => <Textarea id={id} rows={12} className={cn('font-mono text-xs')} value={edit.content} onChange={e => setEdit({ ...edit, content: e.target.value })} />}
          </Field>
        </div>}
      </Modal>
    </div>
  )
}
